import { useFormatters } from '@/hooks/useFormatters';
import { useGlobalModal } from '@/hooks/useGlobalModal';
import { useSuspenseQuery } from '@apollo/client';
import GlobalModal from '@components/GlobalModal';
import ModalContent from '@components/modal/ModalContent';
import TruncatedTypography from '@components/TruncatedTypography';
import { Access, AuthorizationWrapper } from '@features/authorization';
import StandardAutocompleteIdsWithFallback from '@features/standardDesign/components/autocomplete/StandardAutocompleteIds';
import { t, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { DeleteForever, Unarchive } from '@mui/icons-material';
import { Autocomplete, Box, IconButton, TextField, Tooltip, Typography } from '@mui/material';
import Fuse from 'fuse.js';
import { useMemo, useState, type FC } from 'react';
import { List, RowComponentProps } from 'react-window';
import GET_USERS_GRAVEYARD, { GraveyardUser } from '../graphql/queries/getUsersGraveyard';
import { HardDeleteUserModal } from './HardDeleteUserModal';
import { RestoreUserModal } from './RestoreUserModal';

const ORDER_BY_OPTIONS = ['id', 'fullName', 'deletedAt'] as const;

const SuspenseComponent: FC = () => {
  const [filters, setFilters] = useState({
    search: '',
    organizationId: null as number | null,
  });
  const [orderBy, setOrderBy] = useState<(typeof ORDER_BY_OPTIONS)[number]>('deletedAt');
  const [restoreUserId, setRestoreUserId] = useState<number | null>(null);
  const [hardDeleteUserId, setHardDeleteUserId] = useState<number | null>(null);
  const { data } = useSuspenseQuery(GET_USERS_GRAVEYARD);
  const { i18n } = useLingui();

  const restoreUser = data.users.find((u) => u.id === restoreUserId);
  const hardDeleteUser = data.users.find((u) => u.id === hardDeleteUserId);

  const filteredUsers = useMemo(() => {
    let filteredItems = [...data.users];

    if (orderBy === 'deletedAt') {
      filteredItems.sort((a, b) => {
        const deletedA = a.deletedAt ? new Date(a.deletedAt).getTime() : 0;
        const deletedB = b.deletedAt ? new Date(b.deletedAt).getTime() : 0;
        return deletedB - deletedA;
      });
    }

    if (orderBy === 'fullName') {
      filteredItems.sort((a, b) =>
        a.fullName.toLowerCase().localeCompare(b.fullName.toLowerCase()),
      );
    }

    if (filters.organizationId != null) {
      filteredItems = filteredItems.filter(
        (user) => user.mainOrganization?.organization.id === filters.organizationId,
      );
    }

    if (filters.search) {
      const fuse = new Fuse(filteredItems, { keys: ['fullName', 'email'] });
      filteredItems = fuse.search(filters.search).map(({ item }) => item);
    }

    return filteredItems;
  }, [filters, data.users, orderBy]);

  return (
    <Box>
      <RestoreUserModal
        open={restoreUserId !== null}
        onClose={() => setRestoreUserId(null)}
        userId={restoreUserId}
        userFullName={restoreUser?.fullName}
      />
      <HardDeleteUserModal
        open={hardDeleteUserId !== null}
        onClose={() => setHardDeleteUserId(null)}
        userId={hardDeleteUserId}
        userFullName={hardDeleteUser?.fullName}
      />
      <Box mb={1} display="grid" gridTemplateColumns="1fr 1fr 1fr" gap={1}>
        <TextField
          value={filters.search}
          onChange={(event) =>
            setFilters((filters) => ({ ...filters, search: event.target.value }))
          }
          label={t(i18n)`Buscar`}
          size="small"
        />
        <StandardAutocompleteIdsWithFallback
          value={filters.organizationId}
          onChange={(_, organizationId) =>
            setFilters((filters) => ({ ...filters, organizationId }))
          }
          size="small"
          dataType="organizations"
        />
        <Autocomplete
          value={orderBy}
          disableClearable
          onChange={(_, value) => setOrderBy(value)}
          options={ORDER_BY_OPTIONS}
          getOptionLabel={(option) => {
            if (option === 'id') return 'ID';
            if (option === 'fullName') return t(i18n)`Nombre`;
            if (option === 'deletedAt') return t(i18n)`Fecha de archivado`;
            return option;
          }}
          size="small"
          renderInput={(params) => <TextField {...params} label={t(i18n)`Ordenar`} />}
        />
      </Box>
      <Box border={1} borderColor="divider" borderRadius={1} height={550}>
        <List
          rowCount={filteredUsers.length}
          rowHeight={42}
          rowComponent={RowComponent}
          rowProps={{
            users: filteredUsers,
            openRestoreUserModal: setRestoreUserId,
            openHardDeleteUserModal: setHardDeleteUserId,
          }}
        />
      </Box>
    </Box>
  );
};

const RowComponent: FC<
  RowComponentProps<{
    users: GraveyardUser[];
    openRestoreUserModal: (userId: number) => void;
    openHardDeleteUserModal: (userId: number) => void;
  }>
> = ({ index, style, users, openRestoreUserModal, openHardDeleteUserModal }) => {
  const { i18n } = useLingui();
  const { formatDateTime } = useFormatters();

  const user = users[index];

  return (
    <Box
      style={style}
      key={user.id}
      display="grid"
      gridTemplateColumns="150px minmax(0, 1fr) auto"
      gap={2}
      borderBottom={index < users.length - 1 ? 1 : 0}
      borderColor="divider"
      px={1}
      py="2px"
      alignItems="center"
    >
      <Box borderRight={1} fontSize="small" borderColor="divider">
        {user.deletedAt ? (
          <Typography fontWeight="bold" color="error.light" fontSize="inherit">
            {formatDateTime(new Date(user.deletedAt).getTime(), {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
            })}
          </Typography>
        ) : (
          <Typography fontWeight="bold" color="error.light" fontSize="inherit">
            <Trans>Sin información</Trans>
          </Typography>
        )}
      </Box>

      <Box>
        <Box display="flex" gap={2}>
          <TruncatedTypography fontSize="14px">{user.fullName}</TruncatedTypography>
          <TruncatedTypography fontSize="14px" color="text.secondary">
            {user.mainOrganization?.organization.name}
          </TruncatedTypography>
        </Box>
        <TruncatedTypography fontSize="11px" color="text.secondary">
          {user.email}
        </TruncatedTypography>
      </Box>

      <Box display="flex" gap={1}>
        <Tooltip title={t(i18n)`Desarchivar`}>
          <span>
            <IconButton size="small" onClick={() => openRestoreUserModal(user.id)}>
              <Unarchive fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <AuthorizationWrapper access={Access.HardDeleteUser} fallback={null}>
          <Tooltip title={t(i18n)`Eliminar definitivamente`}>
            <span>
              <IconButton size="small" color="error" onClick={() => openHardDeleteUserModal(user.id)}>
                <DeleteForever fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </AuthorizationWrapper>
      </Box>
    </Box>
  );
};

export const UsersGraveyard: FC = () => {
  const { globalModalProps, onClose } = useGlobalModal('usersGraveyardModal');

  return (
    <GlobalModal
      {...globalModalProps}
      onClose={onClose}
      dialogProps={{ maxWidth: 'lg' }}
      authorization={{
        access: Access.ViewDeletedUsers,
        onClose,
      }}
      modalTitleProps={{
        title: <Trans>Cementerio de usuarios</Trans>,
        onClose,
      }}
    >
      <ModalContent topDivider>
        <SuspenseComponent />
      </ModalContent>
    </GlobalModal>
  );
};
